import axios, { AxiosResponse } from 'axios';
import queryString from 'qs';
import {
  ENDPOINT_LOGOUT,
  ENDPOINT_RSI_GET_ME,
  LOCALHOST,
} from 'constants/constants';
import { errorOf } from './error';
import { LoginFunction } from 'components/providers/loginContext';
import { RsiUserObject } from 'models/rsiUserobject';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.tz.setDefault('Asia/Tokyo');

export async function getAPI<T>(
  path: string,
  loginContext: LoginFunction,
  params?: Record<string, unknown>,
  customHeader?: any,
  controller?: AbortController
): Promise<T> {
  const abortController = controller || new AbortController();
  // ヘッダーを定義
  const headers =
    window.location.origin !== LOCALHOST
      ? undefined
      : await loginContext.getLocalAuthHeader();

  const config = {
    withCredentials: true,
    signal: abortController.signal,
    headers: headers as any,
  };
  config.headers = { ...customHeader, ...config.headers };

  const query = params && queryString.stringify(params);
  const url = query ? `${path}?${query}` : path;

  // リクエスト
  return await axios
    .get(url, config)
    .then((res: AxiosResponse) => {
      return res.data;
    })
    .catch(async (e: any) => {
      // Abortされた場合はthrowする
      if (e.message === 'canceled') {
        throw e;
      }
      throw await errorOf(e.response);
    });
}

export async function postAPI<T>(
  path: string,
  loginContext: LoginFunction,
  body?: { [key: string]: any },
  controller?: AbortController
): Promise<T> {
  const abortController = controller || new AbortController();
  // ヘッダーを定義
  const headers =
    window.location.origin !== LOCALHOST
      ? undefined
      : await loginContext.getLocalAuthHeader();

  const config = {
    withCredentials: true,
    signal: abortController.signal,
    headers: headers as any,
  };

  // リクエスト
  return axios
    .post(path, body, config)
    .then((res: AxiosResponse) => {
      return res.data;
    })
    .catch(async (e: any) => {
      // Abortされた場合はthrowする
      if (e.message === 'canceled') {
        throw e;
      }
      throw await errorOf(e.response);
    });
}

export async function putAPI<T>(
  path: string,
  loginContext: LoginFunction,
  body: { [key: string]: any },
  controller?: AbortController
): Promise<T> {
  const abortController = controller || new AbortController();
  // ヘッダーを定義
  const headers =
    window.location.origin !== LOCALHOST
      ? undefined
      : await loginContext.getLocalAuthHeader();

  const config = {
    withCredentials: true,
    signal: abortController.signal,
    headers: headers as any,
  };

  // リクエスト
  return axios
    .put(path, body, config)
    .then((res: AxiosResponse) => {
      return res.data;
    })
    .catch(async (e: any) => {
      // Abortされた場合はthrowする
      if (e.message === 'canceled') {
        throw e;
      }
      throw await errorOf(e.response);
    });
}

// RSIのAPIを呼ぶために作成
export async function getRSI<T>(path: string, accessToken: string): Promise<T> {
  // ヘッダーを定義
  const config = {
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: 'Bearer ' + accessToken,
    },
  };
  // リクエスト
  return axios
    .get(path, config)
    .then((res: AxiosResponse) => {
      return res.data;
    })
    .catch(async (e: any) => {
      throw await errorOf(e.response);
    });
}

export async function logout() {
  const config = { withCredentials: true };
  await axios.get(ENDPOINT_LOGOUT, config).catch(async (e: any) => {
    throw await errorOf(e.response);
  });
}

export async function createLocalAuthHeaders(token: string) {
  const headers = await getRSI<RsiUserObject>(ENDPOINT_RSI_GET_ME, token).then(
    (res: RsiUserObject) => {
      // jwt形式で取得したユーザー情報を代入
      const userInfo = {
        ext: res,
      };
      const jsonStr = JSON.stringify(userInfo);
      const encoded = Buffer.from(jsonStr, 'utf8').toString('base64');
      const data = `.${encoded}.`;

      const headers = {
        'x-amzn-oidc-accesstoken': token,
        'x-amzn-oidc-data': data,
      };
      return headers;
    }
  );
  return headers;
}

// UUID を生成する
export function generateUuid() {
  return crypto.randomUUID();
}

// 日付を指定したフォーマットに変換する
export function convertDate(date: string, format: string) {
  return dayjs(date).tz().format(format);
}
